import { Injectable } from '@angular/core';

import { FruHttpService } from './fru-http.service';
import { AnalyticsService } from './analytics.service';

// FRU-22871: one-to-one consent funnel diagnostics.
//
// Every consent attempt (per encrypted lead id) gets a UUID attempt_id persisted in
// sessionStorage, so a refresh or a stale-link redirect keeps measuring the SAME attempt
// for that lead id (a redirect to a different lead id starts a fresh attempt).
//
// Each record() does two things, both strictly non-blocking:
//   1. a first-party diagnostic POST to selected_manager/consent_attempt_event (always sent —
//      this is server-side diagnostics, not third-party analytics), and
//   2. a privacy-safe GA4 + Matomo mirror via AnalyticsService.trackConsentAttempt (opt-out is
//      respected indirectly by delegating to AnalyticsService rather than touching gtag/_paq here).
//
// PRIVACY: payloads carry only coarse diagnostic tokens (stage/outcome/reason), ids the server
// itself issued (encrypted lead id, our own attempt/submission UUIDs), coarse device/browser
// classes, and analytics ids. Phone, email, Jornaya/TrustedForm tokens and full URLs are never
// included; free-form metadata values are dropped when they look like contact data.

export type ConsentAttemptOutcome = 'success' | 'failure' | 'unknown';

export interface ConsentAttemptEventOptions {
  stage: string;
  outcome: ConsentAttemptOutcome;
  source?: string;
  reason?: string;
  httpStatus?: number;
  submissionId?: string;
  requestId?: string;
  originalRequestId?: string;
  metadata?: { [key: string]: string | number | boolean | null };
}

interface ConsentAttemptRecord {
  attemptId: string;
  startedAt: number;
  channel: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConsentAttemptTelemetryService {
  private static readonly STORAGE_PREFIX = 'fru22871ConsentAttempt:';
  private static readonly SOURCE = 'one_to_one_consent';
  // No build-stamped release exists in the bundle today (Sentry has no release either), so the
  // surface + framework major is the most precise stable label available.
  private static readonly FRONTEND_RELEASE = 'www-ng';
  private static readonly CHANNELS = ['sms', 'email', 'web'];

  private readonly deviceClass: string;
  private readonly browserClass: string;
  // Fallback when sessionStorage is unavailable (private mode / disabled): attempts still work
  // in-memory for the life of the page.
  private readonly memoryAttempts = new Map<string, ConsentAttemptRecord>();

  constructor(
    private fruHttp: FruHttpService,
    private analyticsService: AnalyticsService,
  ) {
    const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
    this.deviceClass = ConsentAttemptTelemetryService.detectDeviceClass(userAgent);
    this.browserClass = ConsentAttemptTelemetryService.detectBrowserClass(userAgent);
  }

  /**
   * Create (or restore) the attempt for an encrypted consent lead id and return its attempt_id.
   * Idempotent: an existing attempt is restored unchanged, so elapsed_ms keeps counting from the
   * original start and the channel is not overwritten.
   */
  startAttempt(encryptedLeadId: string, channel: string = ''): string {
    if (!encryptedLeadId) { return ''; }
    try {
      const existing = this.loadAttempt(encryptedLeadId);
      if (existing) { return existing.attemptId; }

      const record: ConsentAttemptRecord = {
        attemptId: this.createUuid(),
        startedAt: Date.now(),
        channel: ConsentAttemptTelemetryService.channelToken(channel),
      };
      this.saveAttempt(encryptedLeadId, record);
      return record.attemptId;
    } catch (e) {
      return '';
    }
  }

  /**
   * Record one funnel event. Never throws and never blocks the caller: transport/analytics
   * failures are swallowed here (the first-party POST is caught at THIS call site, per the
   * FRU-22871 contract — FruHttpService.consentAttemptEvent itself stays a plain POST).
   */
  record(encryptedLeadId: string, event: ConsentAttemptEventOptions): void {
    if (!event || !event.stage) { return; }

    try {
      const payload = this.buildPayload(encryptedLeadId, event);
      const request = this.fruHttp.consentAttemptEvent(payload);
      if (request && typeof request.subscribe === 'function') {
        request.subscribe({ error: () => { /* telemetry failures are non-blocking */ } });
      }
    } catch (e) { /* diagnostics must never break the consent flow */ }

    try {
      this.analyticsService.trackConsentAttempt(event.stage, event.outcome, event.reason || '');
    } catch (e) { /* non-fatal */ }
  }

  recordOnUnload(encryptedLeadId: string, event: ConsentAttemptEventOptions): void {
    if (!event || !event.stage) { return; }

    try {
      this.fruHttp.consentAttemptEventOnUnload(this.buildPayload(encryptedLeadId, event));
    } catch (e) { /* diagnostics must never break the consent flow */ }

    try {
      this.analyticsService.trackConsentAttempt(event.stage, event.outcome, event.reason || '');
    } catch (e) { /* non-fatal */ }
  }

  private buildPayload(encryptedLeadId: string, event: ConsentAttemptEventOptions): any {
    const attempt = encryptedLeadId ? this.loadAttempt(encryptedLeadId) : null;
    const payload: any = {
      attempt_id: attempt ? attempt.attemptId : '',
      encryptedLeadId: ConsentAttemptTelemetryService.cleanValue(encryptedLeadId, 256),
      channel: attempt ? attempt.channel : 'web',
      stage: ConsentAttemptTelemetryService.token(event.stage, 48),
      outcome: ConsentAttemptTelemetryService.token(event.outcome, 16) || 'unknown',
      elapsed_ms: attempt ? Math.max(0, Date.now() - attempt.startedAt) : 0,
      observed_at: Math.floor(Date.now() / 1000),
      frontend_release: ConsentAttemptTelemetryService.FRONTEND_RELEASE,
      device_class: this.deviceClass,
      browser_class: this.browserClass,
      source: ConsentAttemptTelemetryService.token(event.source, 40) || ConsentAttemptTelemetryService.SOURCE,
    };

    if (event.reason) { payload.reason = ConsentAttemptTelemetryService.token(event.reason, 64); }
    if (event.httpStatus !== undefined && event.httpStatus !== null) {
      const status = Number(event.httpStatus);
      payload.http_status = Number.isFinite(status) ? status : 0;
    }
    if (event.submissionId) { payload.submission_id = ConsentAttemptTelemetryService.cleanValue(event.submissionId, 64); }
    if (event.requestId) { payload.request_id = ConsentAttemptTelemetryService.cleanValue(event.requestId, 256); }
    if (event.originalRequestId) { payload.original_request_id = ConsentAttemptTelemetryService.cleanValue(event.originalRequestId, 256); }

    const analyticsIds = this.analyticsIds();
    if (analyticsIds.analyticsClientId) { payload.analytics_client_id = analyticsIds.analyticsClientId; }
    if (analyticsIds.matomoVisitorId) { payload.matomo_visitor_id = analyticsIds.matomoVisitorId; }

    const metadata = this.sanitizeMetadata(event.metadata);
    if (metadata) { payload.metadata = metadata; }

    return payload;
  }

  // Coarse, low-cardinality device class from the user agent.
  private static detectDeviceClass(userAgent: string): string {
    const ua = (userAgent || '').toLowerCase();
    if (!ua) { return 'unknown'; }
    if (/ipad|tablet|playbook|silk/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua))) { return 'tablet'; }
    if (/mobi|iphone|ipod|windows phone/.test(ua) || (/android/.test(ua) && /mobile/.test(ua))) { return 'mobile'; }
    return 'desktop';
  }

  // Coarse browser family. Order matters: Edge/Opera/brave-style UAs all contain "chrome",
  // so the more specific tokens are matched first.
  private static detectBrowserClass(userAgent: string): string {
    const ua = (userAgent || '').toLowerCase();
    if (!ua) { return 'unknown'; }
    if (/edg\//.test(ua) || /edge\//.test(ua)) { return 'edge'; }
    if (/opr\/|opera/.test(ua)) { return 'opera'; }
    if (/firefox|fxios/.test(ua)) { return 'firefox'; }
    if (/chrome|crios/.test(ua)) { return 'chrome'; }
    if (/safari/.test(ua)) { return 'safari'; }
    return 'other';
  }

  private static channelToken(raw: string): string {
    const value = (raw || '').toString().toLowerCase().trim();
    return ConsentAttemptTelemetryService.CHANNELS.indexOf(value) >= 0 ? value : 'web';
  }

  // Diagnostic tokens (stage/outcome/reason): restricted charset so no free-form server or
  // client string can smuggle punctuation/PII into the event.
  private static token(raw: any, limit: number): string {
    if (raw === null || raw === undefined || typeof raw === 'object') { return ''; }
    return String(raw).replace(/[^A-Za-z0-9_]/g, '').slice(0, limit);
  }

  // Ids (server-issued encrypted lead ids, our UUIDs, cookie ids): control chars stripped and
  // length capped, but charset preserved so the join keys survive verbatim.
  private static cleanValue(raw: any, limit: number): string {
    if (raw === null || raw === undefined || typeof raw === 'object') { return ''; }
    return String(raw).replace(/[\x00-\x1F\x7F]/g, '').trim().slice(0, limit);
  }

  private static looksLikeContactData(value: string): boolean {
    return /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value)
      || /(?:\+?\d[\s().-]*){10,}/.test(value);
  }

  private sanitizeMetadata(metadata: { [key: string]: string | number | boolean | null } | undefined): any {
    if (!metadata || typeof metadata !== 'object') { return null; }
    const safe: any = {};
    Object.keys(metadata).forEach((rawKey) => {
      const key = ConsentAttemptTelemetryService.token(rawKey, 48);
      if (!key) { return; }
      const raw = metadata[rawKey];
      if (raw === null || raw === undefined || typeof raw === 'object') { return; }
      if (typeof raw === 'number') {
        if (Number.isFinite(raw)) { safe[key] = raw; }
        return;
      }
      if (typeof raw === 'boolean') { safe[key] = raw; return; }
      const value = ConsentAttemptTelemetryService.cleanValue(raw, 191);
      if (value && !ConsentAttemptTelemetryService.looksLikeContactData(value)) { safe[key] = value; }
    });
    return Object.keys(safe).length ? safe : null;
  }

  // GA client id + Matomo visitor id for joining diagnostics with analytics. Skipped entirely
  // when the visitor has opted out of cookies (same fruRemoveCookies gate as the conversation
  // context) — the first-party diagnostic POST itself is still sent.
  private analyticsIds(): { analyticsClientId: string; matomoVisitorId: string } {
    const empty = { analyticsClientId: '', matomoVisitorId: '' };
    try {
      const cookie = typeof document !== 'undefined' ? (document.cookie || '') : '';
      const cookies: { [key: string]: string } = {};
      cookie.split(';').forEach((part) => {
        const eq = part.indexOf('=');
        if (eq < 0) { return; }
        const key = part.slice(0, eq).trim();
        if (!key) { return; }
        try { cookies[key] = decodeURIComponent(part.slice(eq + 1).trim()); }
        catch (e) { /* malformed cookie carries no id */ }
      });
      if (Object.prototype.hasOwnProperty.call(cookies, 'fruRemoveCookies')) { return empty; }

      const ga = ConsentAttemptTelemetryService.cleanValue(cookies['_ga'], 128);
      const matomoKey = Object.keys(cookies).find((key) => key.indexOf('_pk_id.') === 0);
      const matomo = matomoKey
        ? ConsentAttemptTelemetryService.cleanValue((cookies[matomoKey] || '').split('.')[0], 64)
        : '';
      return {
        analyticsClientId: (ga && !ConsentAttemptTelemetryService.looksLikeContactData(ga)) ? ga : '',
        matomoVisitorId: (matomo && !ConsentAttemptTelemetryService.looksLikeContactData(matomo)) ? matomo : '',
      };
    } catch (e) {
      return empty;
    }
  }

  private loadAttempt(encryptedLeadId: string): ConsentAttemptRecord | null {
    try {
      const raw = typeof window !== 'undefined' && window.sessionStorage
        ? window.sessionStorage.getItem(ConsentAttemptTelemetryService.STORAGE_PREFIX + encryptedLeadId)
        : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.attemptId && typeof parsed.startedAt === 'number') {
          return parsed as ConsentAttemptRecord;
        }
      }
    } catch (e) { /* fall through to memory */ }
    return this.memoryAttempts.get(encryptedLeadId) || null;
  }

  private saveAttempt(encryptedLeadId: string, record: ConsentAttemptRecord): void {
    this.memoryAttempts.set(encryptedLeadId, record);
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(
          ConsentAttemptTelemetryService.STORAGE_PREFIX + encryptedLeadId,
          JSON.stringify(record),
        );
      }
    } catch (e) { /* memory copy above keeps the attempt alive */ }
  }

  private createUuid(): string {
    try {
      const browserCrypto: any = (typeof window !== 'undefined' ? (window as any).crypto : null);
      if (browserCrypto && browserCrypto.randomUUID) { return browserCrypto.randomUUID(); }
    } catch (e) { /* fall back below */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const n = Math.floor(Math.random() * 16);
      return (ch === 'x' ? n : ((n & 0x3) | 0x8)).toString(16);
    });
  }
}
